import { useEffect, useRef } from 'react';

/**
 * モーダル内でフォーカスをトラップし、Tab/Shift+Tabでフォーカスを循環させるカスタムフック
 * @param {boolean} isActive - フォーカストラップが有効かどうか
 * @returns {React.RefObject} - モーダルコンテナのref
 */
export const useFocusTrap = (isActive = true) => {
  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    // モーダルが開いたときの前のアクティブ要素を保存
    previousActiveElement.current = document.activeElement;

    // フォーカス可能な要素のセレクター
    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), ' +
      'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () => {
      if (!containerRef.current) return [];
      return Array.from(containerRef.current.querySelectorAll(focusableSelector))
        .filter(el => {
          // 非表示要素を除外
          return el.offsetParent !== null;
        });
    };

    // 最初のフォーカス可能要素にフォーカスを設定
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: 最初の要素にいる場合は最後の要素へ
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: 最後の要素にいる場合は最初の要素へ
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);

    // クリーンアップ: モーダルが閉じたら前のフォーカスを復元
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive]);

  return containerRef;
};
